import { ProficiencyLevel, User, UserSettings } from '@beconfident/api-lib';
import axios from 'axios';
import { defineStore } from 'pinia';

import notifyError from '@/helpers/notify-error';
import logger from '@/infra/logger';

import { useOnboardingStore } from './onboarding';

type State = {
  token: string;
  user: User;
  deviceToken: string | null;
  lastProficiencyLevel?: ProficiencyLevel | undefined | null;
};

const emptyUser: User = {
  _id: '',
  fullName: '',
  firstName: '',
  lastName: '',
  whatsapp: '',
  email: '',
  proficiencyLevel: undefined,
  proficiencyLevelUpdatedAt: undefined,
  features: [],
  interestsItems: [],
  onboardingForm: {
    difficulties: [],
    profession: '',
    gender: '',
    age: '',
    fluencyMonthsExpectation: 0,
    motivation: '',
  },
  settings: {
    voiceId: '',
    audioSpeed: 0,
    timezone: '',
    conversation: {
      immediateFeedback: false,
    },
    reminder: {
      types: {
        weeklySummary: false,
        dailyUse: false,
        finishConversations: false,
      },
      hour: undefined,
      enabled: false,
      kind: 'dynamic',
    },
  },
  purchase: {
    status: '',
  },
  referral: {
    link: '',
    stats: {
      visits: 0,
      signUps: 0,
      purchases: 0,
    },
  },
  createdAt: new Date(),
  profile: {
    age: {
      min: 0,
      max: 0,
    },
    profession: '',
    gender: 'other',
    motivation: 'forSelfKnowledge',
    difficulties: [],
    practiceTimeDailyInMinutes: 0,
    interests: [],
    filled: false,
  },
  nativeLanguage: '',
};

export const useAuthStore = defineStore('auth', {
  persist: true,
  state: (): State => ({
    token: '',
    user: emptyUser,
    deviceToken: null,
    lastProficiencyLevel: undefined,
  }),

  actions: {
    async authGoogle({ idtoken }: { idtoken: string }) {
      try {
        const { data } = await axios.post('/auth/google', {
          idtoken,
        });

        this.setToken(idtoken);

        return data.token;
      } catch (error) {
        logger.captureException(error);
        notifyError(error, 'Não foi possível fazer login Google');
      }
    },

    async authApple({ idtoken }: { idtoken: string }) {
      try {
        const { data } = await axios.post('/auth/apple', {
          idtoken,
        });
        this.setToken(idtoken);

        return data.token;
      } catch (error) {
        logger.captureException(error);

        return notifyError(error, 'Não foi possível fazer login Apple');
      }
    },

    async sendOTP({ whatsapp }: { whatsapp: string }) {
      const { data } = await axios.post('/auth/otp/send', {
        whatsapp,
      });

      if (data.whatsappVerified) {
        whatsapp = data.whatsappVerified;
      }

      const onboardingStore = useOnboardingStore();
      onboardingStore.setWhatsapp(whatsapp);

      return data;
    },

    async validateOTP({
      whatsapp,
      code,
      deviceToken,
    }: {
      whatsapp: string;
      code: string;
      deviceToken: string | null;
    }) {
      try {
        const { data } = await axios.post('/auth/otp/validate', {
          whatsapp,
          code,
          deviceToken,
        });

        this.setToken(data.token);

        return data;
      } catch (error) {
        logger.captureException(error);
        notifyError(error, 'Não foi possível validar esse código');
      }
    },

    async getMe(): Promise<User | undefined> {
      const onboardingStore = useOnboardingStore();

      try {
        const response = await this.$apiClient.getMe();

        if (!response) {
          throw new Error('No value answer in getMe');
        }

        // Prevent set last proficiency level if is the first time the user is logged in
        if (
          this.user.proficiencyLevel !== response.proficiencyLevel &&
          this.user._id
        ) {
          this.setLastProficiencyLevel(this.user.proficiencyLevel ?? undefined);
        }

        this.setUser(response);

        onboardingStore.resetOnboarding();

        return response;
      } catch (error) {
        logger.captureException(error);
        notifyError(
          error,
          'Não foi possível carregar as informações do seu perfil',
        );
      }
    },

    async updateMe(
      user: Pick<User, 'fullName' | 'email' | 'profile' | 'onboardingForm'>,
    ) {
      try {
        return await this.$apiClient.updateMe({
          fullName: user.fullName ?? undefined,
          email: user.email ?? undefined,
          interests: user.profile.interests ?? undefined,
          onboardingForm: {
            difficulties: user.onboardingForm.difficulties,
            profession: user.onboardingForm.profession,
            gender: user.onboardingForm.gender,
            age: user.onboardingForm.age,
            fluencyMonthsExpectation:
              Number(user.onboardingForm.fluencyMonthsExpectation) || undefined,
            motivation: user.onboardingForm.motivation,
          },
        });
      } catch (error) {
        logger.captureException(error);
        notifyError(error, 'Não foi possível atualizar seu perfil');
      }
    },

    async updateSettings(params: UserSettings) {
      try {
        const response = await this.$apiClient.updateSettings(params);
        this.setSettings(response);

        return response;
      } catch (error) {
        logger.captureException(error);
        notifyError(error, 'Não foi possível atualizar suas configurações');
      }
    },

    async deleteMe() {
      try {
        // eslint-disable-next-line unicorn/no-useless-undefined
        await this.$apiClient.deleteMe(undefined);

        this.cleanTokenAndUser();
      } catch (error) {
        logger.captureException(error);

        return notifyError(error, 'Não foi possível excluir sua conta');
      }
    },

    async logout() {
      try {
        await this.$apiClient.logout();
      } catch (error) {
        logger.captureException(error);
      } finally {
        this.cleanTokenAndUser();
      }
    },

    async cleanTokenAndUser() {
      this.setToken('');
      this.resetUser();
      this.setLastProficiencyLevel(null);
    },

    setToken(token: string) {
      if (token) {
        this.cleanTokenAndUser();
      }

      this.token = token;
    },
    setDeviceToken(deviceToken: string) {
      this.deviceToken = deviceToken;
    },
    setUser(user: User) {
      this.user = user;
    },
    setLastProficiencyLevel(
      proficiencyLevel: ProficiencyLevel | undefined | null,
    ) {
      this.lastProficiencyLevel = proficiencyLevel;
    },
    setSettings(settings: UserSettings) {
      this.user.settings = settings;
    },
    resetUser() {
      this.user = { ...emptyUser };
    },
    editUserName(fullname: string | null | undefined) {
      this.user.fullName = fullname;
    },
    editUserEmail(email: string | null | undefined) {
      this.user.email = email;
    },
    editUserProfession(profession: string | null | undefined) {
      this.user.onboardingForm.profession = profession;
    },
    editUserAge(ageInterval: string | null | undefined) {
      this.user.onboardingForm.age = ageInterval;
    },
    editUserGender(gender: string | null | undefined) {
      this.user.onboardingForm.gender = gender;
    },
  },

  getters: {
    getAuthVoiceId(state) {
      return state.user.settings.voiceId;
    },
  },
});
